import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
} from '@arco-design/web-react'
import { IconRefresh, IconSearch } from '@arco-design/web-react/icon'
import {
  fetchRatioSyncDiffApi,
  getRatioSyncChannelsApi,
  type RatioSyncDifferenceItem,
} from '@/api/ratioSync'
import { Pill } from '@/utils/render'
import type { OptionsAPI } from '../useOptions'
import ChannelSelectorModal, {
  MODELS_DEV_PRESET_BASE_URL,
  MODELS_DEV_PRESET_ID,
  MODELS_DEV_PRESET_NAME,
  OFFICIAL_RATIO_PRESET_BASE_URL,
  OFFICIAL_RATIO_PRESET_ID,
  OFFICIAL_RATIO_PRESET_NAME,
  type ChannelSelectorModalRef,
  type ChannelTransferItem,
} from './ChannelSelectorModal'
import {
  PRICING_KEYS,
  RATIO_KEYS,
  clearModelPrice,
  clearModelRatios,
  createEmptyPricingMaps,
  getBillingCategory,
  parsePricingMapsFromOptions,
  ratioTypeToOptionKey,
  type PricingMaps,
} from './pricingKeys'

const DEFAULT_ENDPOINT = '/api/ratio_config'
const MODELS_DEV_PRESET_ENDPOINT = 'https://models.dev/api.json'
const OFFICIAL_RATIO_PRESET_ENDPOINT = '/llm-metadata/api/newapi/ratio_config-v1-base.json'

interface DiffRow {
  key: string
  model: string
  ratioType: string
  current: unknown
  upstreams: Record<string, unknown>
  confidence: Record<string, boolean>
  billingConflict: boolean
}

interface ConflictItem {
  channel: string
  model: string
  current: string
  newVal: string
  affectedKeys: string[]
}

function clonePricingMaps(maps: PricingMaps): PricingMaps {
  return PRICING_KEYS.reduce<PricingMaps>((acc, key) => {
    acc[key] = { ...maps[key] }
    return acc
  }, createEmptyPricingMaps())
}

function loadCurrentPricingMaps(options: OptionsAPI): { maps: PricingMaps; errors: Record<string, string> } {
  return parsePricingMapsFromOptions(options.values)
}

function getLocalBillingCategory(maps: PricingMaps, model: string): 'price' | 'ratio' | null {
  if (maps.ModelPrice[model] !== undefined) return 'price'
  if (RATIO_KEYS.some((key) => maps[key][model] !== undefined)) return 'ratio'
  return null
}

function buildAffectedKeys(localCat: 'price' | 'ratio', newCat: 'price' | 'ratio'): string[] {
  if (localCat === newCat) return []
  return ['ModelPrice', ...RATIO_KEYS]
}

export default function UpstreamRatioSyncSection({ options }: { options: OptionsAPI }) {
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [allChannels, setAllChannels] = useState<ChannelTransferItem[]>([])
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([])
  const [channelEndpoints, setChannelEndpoints] = useState<Record<number, string>>({})
  const [differences, setDifferences] = useState<Record<string, Record<string, RatioSyncDifferenceItem>>>({})
  const [resolutions, setResolutions] = useState<Record<string, Record<string, unknown>>>({})
  const [hasSynced, setHasSynced] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [ratioTypeFilter, setRatioTypeFilter] = useState<string>('')
  const [conflictOnly, setConflictOnly] = useState(false)
  const [lowConfidenceOnly, setLowConfidenceOnly] = useState(false)
  const [unsetOnly, setUnsetOnly] = useState(false)
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [conflictItems, setConflictItems] = useState<ConflictItem[]>([])
  const [pendingLocalPatch, setPendingLocalPatch] = useState<Record<string, string> | null>(null)
  const channelSelectorRef = useRef<ChannelSelectorModalRef>(null)

  useEffect(() => {
    setCurrentPage(1)
  }, [ratioTypeFilter, searchKeyword, conflictOnly, lowConfidenceOnly, unsetOnly])

  const fetchAllChannels = async () => {
    setLoading(true)
    try {
      const channels = await getRatioSyncChannelsApi()
      const transferData: ChannelTransferItem[] = (channels || []).map((channel) => ({
        key: channel.id,
        label: channel.name,
        value: channel.id,
        disabled: false,
        _originalData: channel,
      }))
      setAllChannels(transferData)
      setChannelEndpoints((prev) => {
        const merged = { ...prev }
        transferData.forEach((channel) => {
          const id = channel.key
          const base = channel._originalData?.base_url || ''
          const name = channel.label || ''
          const channelType = channel._originalData?.type
          const isOfficialRatioPreset =
            id === OFFICIAL_RATIO_PRESET_ID || base === OFFICIAL_RATIO_PRESET_BASE_URL || name === OFFICIAL_RATIO_PRESET_NAME
          const isModelsDevPreset =
            id === MODELS_DEV_PRESET_ID || base === MODELS_DEV_PRESET_BASE_URL || name === MODELS_DEV_PRESET_NAME
          const isOpenRouter = channelType === 20
          if (!merged[id]) {
            if (isModelsDevPreset) merged[id] = MODELS_DEV_PRESET_ENDPOINT
            else if (isOfficialRatioPreset) merged[id] = OFFICIAL_RATIO_PRESET_ENDPOINT
            else if (isOpenRouter) merged[id] = 'openrouter'
            else merged[id] = DEFAULT_ENDPOINT
          }
        })
        return merged
      })
    } catch (err) {
      Message.error(`获取渠道失败：${err instanceof Error ? err.message : '未知错误'}`)
    } finally {
      setLoading(false)
    }
  }

  const confirmChannelSelection = () => {
    const selected = allChannels
      .filter((ch) => selectedChannelIds.includes(ch.value))
      .map((ch) => ch._originalData)
    if (selected.length === 0) {
      Message.warning('请至少选择一个渠道')
      return
    }
    setModalVisible(false)
    fetchRatiosFromChannels(selected)
  }

  const fetchRatiosFromChannels = async (channelList: Array<{ id: number; name: string; base_url?: string }>) => {
    setSyncLoading(true)
    const upstreams = channelList.map((ch) => ({
      id: ch.id,
      name: ch.name,
      base_url: ch.base_url ?? '',
      endpoint: channelEndpoints[ch.id] || DEFAULT_ENDPOINT,
    }))
    try {
      const result = await fetchRatioSyncDiffApi({ upstreams, timeout: 10 })
      const nextDiffs = result.differences ?? {}
      const errorResults = (result.test_results ?? []).filter((r) => r.status === 'error')
      if (errorResults.length > 0) {
        Message.warning(`部分渠道测试失败：${errorResults.map((r) => `${r.name}: ${r.error}`).join(', ')}`)
      }
      setDifferences(nextDiffs)
      setResolutions({})
      setHasSynced(true)
      if (Object.keys(nextDiffs).length === 0) Message.success('未找到差异化倍率，无需同步')
    } catch (err) {
      Message.error(`请求后端接口失败：${err instanceof Error ? err.message : '未知错误'}`)
    } finally {
      setSyncLoading(false)
    }
  }

  const selectValue = useCallback((model: string, ratioType: string, value: unknown) => {
    const category = getBillingCategory(ratioType)
    setResolutions((prev) => {
      const newModelRes = { ...(prev[model] || {}) }
      Object.keys(newModelRes).forEach((rt) => {
        if (getBillingCategory(rt) !== category) delete newModelRes[rt]
      })
      newModelRes[ratioType] = value
      return { ...prev, [model]: newModelRes }
    })
  }, [])

  const applyToLocalDraft = useCallback(
    (currentMaps: PricingMaps) => {
      const finalMaps = clonePricingMaps(currentMaps)
      const modifiedKeys = new Set<string>()

      Object.entries(resolutions).forEach(([model, ratios]) => {
        const selectedTypes = Object.keys(ratios)
        const hasPrice = selectedTypes.includes('model_price')
        const hasRatio = selectedTypes.some((rt) => rt !== 'model_price')

        if (hasPrice) {
          clearModelRatios(finalMaps, model)
          RATIO_KEYS.forEach((key) => modifiedKeys.add(key))
        }
        if (hasRatio) {
          clearModelPrice(finalMaps, model)
          modifiedKeys.add('ModelPrice')
        }

        Object.entries(ratios).forEach(([ratioType, value]) => {
          const optionKey = ratioTypeToOptionKey(ratioType)
          if (!optionKey) {
            Message.warning(`未知倍率类型 ${ratioType}，已跳过`)
            return
          }
          const numericValue = parseFloat(String(value))
          if (!Number.isFinite(numericValue)) return
          finalMaps[optionKey][model] = numericValue
          modifiedKeys.add(optionKey)
        })
      })

      const patchPayload: Record<string, string> = {}
      PRICING_KEYS.forEach((key) => {
        if (modifiedKeys.has(key)) {
          patchPayload[key] = JSON.stringify(finalMaps[key], null, 2)
        }
      })

      options.patch(patchPayload)
      setPendingLocalPatch(patchPayload)

      setDifferences((prevDifferences) => {
        const newDifferences = { ...prevDifferences }
        Object.entries(resolutions).forEach(([model, ratios]) => {
          Object.keys(ratios).forEach((ratioType) => {
            if (newDifferences[model]?.[ratioType]) {
              delete newDifferences[model][ratioType]
              if (Object.keys(newDifferences[model]).length === 0) delete newDifferences[model]
            }
          })
        })
        return newDifferences
      })
      setResolutions({})
      Message.success('已应用到本地草稿，请确认后点击「保存到服务器」')
    },
    [options, resolutions]
  )

  const saveToServer = async () => {
    if (!pendingLocalPatch) {
      Message.warning('暂无待保存的同步草稿')
      return
    }
    setLoading(true)
    try {
      await options.save(Object.keys(pendingLocalPatch), pendingLocalPatch)
      setPendingLocalPatch(null)
      await options.reload()
      Message.success('同步结果已保存到服务器')
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setLoading(false)
    }
  }

  const performSync = useCallback(
    (currentMaps: PricingMaps) => {
      applyToLocalDraft(currentMaps)
    },
    [applyToLocalDraft]
  )

  const applySync = async () => {
    const { maps: currentMaps, errors } = loadCurrentPricingMaps(options)
    if (Object.keys(errors).length > 0) {
      Message.error(`本地定价 JSON 解析失败：${Object.keys(errors).join('、')}，请先修复后再同步`)
      return
    }

    const conflicts: ConflictItem[] = []

    const findSourceChannel = (model: string, ratioType: string, value: unknown) => {
      const upMap = differences[model]?.[ratioType]?.upstreams ?? {}
      const entry = Object.entries(upMap).find(([, v]) => v === value)
      return entry ? entry[0] : '未知'
    }

    Object.entries(resolutions).forEach(([model, ratios]) => {
      const localCat = getLocalBillingCategory(currentMaps, model)
      const newCat = 'model_price' in ratios ? 'price' : 'ratio'
      if (localCat && localCat !== newCat) {
        const currentDesc =
          localCat === 'price'
            ? `固定价格 : ${currentMaps.ModelPrice[model]}`
            : [
                `ModelRatio : ${currentMaps.ModelRatio[model] ?? '-'}`,
                `CompletionRatio : ${currentMaps.CompletionRatio[model] ?? '-'}`,
                `CacheRatio : ${currentMaps.CacheRatio[model] ?? '-'}`,
              ].join('\n')
        let newDesc = ''
        if (newCat === 'price') {
          newDesc = `固定价格 : ${ratios.model_price}`
        } else {
          newDesc = [
            `ModelRatio : ${ratios.model_ratio ?? '-'}`,
            `CompletionRatio : ${ratios.completion_ratio ?? '-'}`,
            `CacheRatio : ${ratios.cache_ratio ?? '-'}`,
          ].join('\n')
        }
        const channels = Object.entries(ratios)
          .map(([rt, val]) => findSourceChannel(model, rt, val))
          .filter((v, idx, arr) => arr.indexOf(v) === idx)
          .join(', ')
        conflicts.push({
          channel: channels,
          model,
          current: currentDesc,
          newVal: newDesc,
          affectedKeys: buildAffectedKeys(localCat, newCat),
        })
      }
    })

    if (conflicts.length > 0) {
      setConflictItems(conflicts)
      setConfirmVisible(true)
      return
    }
    performSync(currentMaps)
  }

  const dataSource = useMemo(() => {
    const tmp: DiffRow[] = []
    Object.entries(differences).forEach(([model, ratioTypes]) => {
      const hasPrice = 'model_price' in ratioTypes
      const hasOtherRatio = ['model_ratio', 'completion_ratio', 'cache_ratio'].some((rt) => rt in ratioTypes)
      const billingConflict = hasPrice && hasOtherRatio
      Object.entries(ratioTypes).forEach(([ratioType, diff]) => {
        tmp.push({
          key: `${model}_${ratioType}`,
          model,
          ratioType,
          current: diff.current,
          upstreams: diff.upstreams ?? {},
          confidence: diff.confidence ?? {},
          billingConflict,
        })
      })
    })
    return tmp
  }, [differences])

  const filteredDataSource = useMemo(() => {
    return dataSource.filter((item) => {
      const matchesKeyword =
        !searchKeyword.trim() || item.model.toLowerCase().includes(searchKeyword.toLowerCase().trim())
      const matchesRatioType = !ratioTypeFilter || item.ratioType === ratioTypeFilter
      const matchesConflict = !conflictOnly || item.billingConflict
      const matchesLowConfidence =
        !lowConfidenceOnly || Object.values(item.confidence || {}).some((value) => value === false)
      const matchesUnset =
        !unsetOnly || item.current === null || item.current === undefined
      return matchesKeyword && matchesRatioType && matchesConflict && matchesLowConfidence && matchesUnset
    })
  }, [conflictOnly, dataSource, lowConfidenceOnly, ratioTypeFilter, searchKeyword, unsetOnly])

  const upstreamNames = useMemo(() => {
    const set = new Set<string>()
    filteredDataSource.forEach((row) => {
      Object.keys(row.upstreams || {}).forEach((name) => set.add(name))
    })
    return Array.from(set)
  }, [filteredDataSource])

  const pagedData = filteredDataSource.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const typeMap: Record<string, string> = {
    model_ratio: '模型倍率',
    completion_ratio: '补全倍率',
    cache_ratio: '缓存倍率',
    model_price: '固定价格',
  }

  const columns = [
    { title: '模型', dataIndex: 'model', fixed: 'left' as const, width: 180 },
    {
      title: '倍率类型',
      dataIndex: 'ratioType',
      width: 140,
      render: (text: string, record: DiffRow) => (
        <Space>
          <Pill variant="info">{typeMap[text] || text}</Pill>
          {record.billingConflict ? (
            <Tooltip content="该模型存在固定价格与倍率计费方式冲突，请确认选择">
              <span className="text-amber-500">⚠</span>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: '置信度',
      width: 100,
      render: (_: unknown, record: DiffRow) => {
        const allConfident = Object.values(record.confidence || {}).every((v) => v !== false)
        return allConfident ? <Pill variant="success">可信</Pill> : <Pill variant="warning">谨慎</Pill>
      },
    },
    {
      title: '当前值',
      dataIndex: 'current',
      width: 120,
      render: (text: unknown) => (
        <Pill variant="neutral">{text !== null && text !== undefined ? String(text) : '未设置'}</Pill>
      ),
    },
    ...upstreamNames.map((upName) => ({
      title: upName,
      render: (_: unknown, record: DiffRow) => {
        const upstreamVal = record.upstreams?.[upName]
        const isConfident = record.confidence?.[upName] !== false
        if (upstreamVal === null || upstreamVal === undefined) return <Pill variant="neutral">未设置</Pill>
        if (upstreamVal === 'same') return <Pill variant="info">与本地相同</Pill>
        const isSelected = resolutions[record.model]?.[record.ratioType] === upstreamVal
        return (
          <Space>
            <Checkbox
              checked={isSelected}
              onChange={(checked) => {
                if (checked) selectValue(record.model, record.ratioType, upstreamVal)
                else {
                  setResolutions((prev) => {
                    const newRes = { ...prev }
                    if (newRes[record.model]) {
                      delete newRes[record.model][record.ratioType]
                      if (Object.keys(newRes[record.model]).length === 0) delete newRes[record.model]
                    }
                    return newRes
                  })
                }
              }}
            >
              {String(upstreamVal)}
            </Checkbox>
            {!isConfident ? <span className="text-amber-500">⚠</span> : null}
          </Space>
        )
      },
    })),
  ]

  return (
    <div className="space-y-3">
      <div className="settings-info-strip">
        当前同步仅覆盖固定价、输入倍率、补全倍率、缓存读取倍率；缓存创建、图片、音频倍率请在「价格设置」中手工维护。
      </div>
      {pendingLocalPatch ? (
        <div className="settings-toolbar settings-sticky-footer">
          <div className="settings-warning-strip flex-1">
            同步结果已写入本地草稿（{Object.keys(pendingLocalPatch).join(' / ')}），确认无误后保存到服务器。
          </div>
          <Button size="small" type="primary" loading={loading} onClick={saveToServer}>
            保存到服务器
          </Button>
        </div>
      ) : null}
      <div className="settings-toolbar">
        <Button
          icon={<IconRefresh />}
          onClick={() => {
            setModalVisible(true)
            if (allChannels.length === 0) fetchAllChannels()
          }}
        >
          选择同步渠道
        </Button>
        <Button type="secondary" disabled={Object.keys(resolutions).length === 0} onClick={applySync}>
          应用到本地草稿
        </Button>
        <Input
          prefix={<IconSearch />}
          placeholder="搜索模型名称"
          value={searchKeyword}
          onChange={setSearchKeyword}
          allowClear
          style={{ width: 260 }}
        />
        <Select
          placeholder="按倍率类型筛选"
          value={ratioTypeFilter || undefined}
          onChange={(value) => setRatioTypeFilter((value as string) || '')}
          allowClear
          style={{ width: 180 }}
        >
          <Select.Option value="model_ratio">模型倍率</Select.Option>
          <Select.Option value="completion_ratio">补全倍率</Select.Option>
          <Select.Option value="cache_ratio">缓存倍率</Select.Option>
          <Select.Option value="model_price">固定价格</Select.Option>
        </Select>
        <Checkbox checked={conflictOnly} onChange={setConflictOnly}>
          只看冲突
        </Checkbox>
        <Checkbox checked={lowConfidenceOnly} onChange={setLowConfidenceOnly}>
          只看低置信度
        </Checkbox>
        <Checkbox checked={unsetOnly} onChange={setUnsetOnly}>
          只看未设置
        </Checkbox>
      </div>

      <div className="settings-section settings-compact-table">
        <Table
        columns={columns}
        data={pagedData}
        rowKey="key"
        loading={loading || syncLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize,
          total: filteredDataSource.length,
          showTotal: true,
          sizeCanChange: true,
          pageSizeChangeResetCurrent: true,
          onChange: (page, size) => {
            setCurrentPage(page)
            setPageSize(size)
          },
        }}
        noDataElement={
          <Empty
            description={
              searchKeyword.trim()
                ? '未找到匹配的模型'
                : Object.keys(differences).length === 0
                  ? hasSynced
                    ? '暂无差异化倍率显示'
                    : '请先选择同步渠道'
                  : '请先选择同步渠道'
            }
          />
        }
        />
      </div>

      <ChannelSelectorModal
        ref={channelSelectorRef}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          channelSelectorRef.current?.resetPagination()
        }}
        onOk={confirmChannelSelection}
        allChannels={allChannels}
        selectedChannelIds={selectedChannelIds}
        setSelectedChannelIds={setSelectedChannelIds}
        channelEndpoints={channelEndpoints}
        updateChannelEndpoint={(channelId, endpoint) =>
          setChannelEndpoints((prev) => ({ ...prev, [channelId]: endpoint }))
        }
      />

      <Modal
        title="确认冲突项修改"
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onOk={() => {
          setConfirmVisible(false)
          const { maps, errors } = loadCurrentPricingMaps(options)
          if (Object.keys(errors).length > 0) {
            Message.error(`本地定价 JSON 解析失败：${Object.keys(errors).join('、')}`)
            return
          }
          performSync(maps)
        }}
        style={{ width: 960 }}
      >
        <Table
          columns={[
            { title: '渠道', dataIndex: 'channel', width: 120 },
            { title: '模型', dataIndex: 'model', width: 160 },
            {
              title: '当前计费',
              dataIndex: 'current',
              render: (text: string) => <div className="whitespace-pre-wrap text-sm">{text}</div>,
            },
            {
              title: '修改为',
              dataIndex: 'newVal',
              render: (text: string) => <div className="whitespace-pre-wrap text-sm">{text}</div>,
            },
            {
              title: '受影响 key',
              dataIndex: 'affectedKeys',
              render: (keys: string[]) => (
                <div className="text-xs font-mono whitespace-pre-wrap">{keys.join('\n')}</div>
              ),
            },
          ]}
          data={conflictItems}
          pagination={false}
        />
      </Modal>
    </div>
  )
}
